import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '@mui/material';
import { tokens } from '../../utils/theme';
import { Panel, PanelHeader, PanelTitle, ScrollRailVertical, ScrollThumbVertical } from '../../components/SharedPageStyles';

// --- Styled Components ---
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  flex: 1;
`;

const CenteredLayout = styled.div`
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ScrollWrapper = styled.div`
  flex: 1;
  width: 100%;
  overflow: auto;
  position: relative;
  
  /* Hide native scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CalculatorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionBlock = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 8px;
  margin-bottom: 4px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 11px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.div`
  font-size: 14px;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  font-weight: 500;
  padding: 8px 10px;
  min-height: 37px; /* Match input height */
  display: flex;
  align-items: center;
`;

const StyledSelect = styled.select`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
  }
  
  option {
    background: ${props => props.theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff'};
  }
`;

const StyledInput = styled.input`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
  }
`;

const RulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RuleItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  background: ${props => props.checked
    ? (props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.05)' : 'rgba(76, 206, 172, 0.1)')
    : 'transparent'};
  border: 1px solid ${props => props.checked
    ? tokens(props.theme.palette.mode).greenAccent[500]
    : tokens(props.theme.palette.mode).grey[700]};
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled ? 0.8 : 1};
  transition: all 0.2s;

  &:hover {
    background: ${props => !props.disabled && (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)')};
  }
`;

const Checkbox = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${props => props.checked
    ? tokens(props.theme.palette.mode).greenAccent[500]
    : tokens(props.theme.palette.mode).grey[500]};
  background: ${props => props.checked
    ? tokens(props.theme.palette.mode).greenAccent[500]
    : 'transparent'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  opacity: ${props => props.disabled ? 0.8 : 1};
  
  &:after {
    content: '✓';
    color: white;
    font-size: 12px;
    display: ${props => props.checked ? 'block' : 'none'};
  }
`;

const RuleText = styled.div`
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  font-size: 0.9rem;
  line-height: 1.4;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
`;

const StatBox = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[700]};
`;

const StatValue = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: ${props => props.color || tokens(props.theme.palette.mode).grey[100]};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  text-transform: uppercase;
`;

const ResponsiveGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

// --- Constants ---
const RULES_BY_PROFILE = {
  risk_high: [
    "I accept higher volatility for higher returns.",
    "I strictly risk 1% per trade, never more.",
    "I stop trading for the day after 2 consecutive losses.",
    "I only take setups with a minimum Risk/Reward of 1:3.",
    "I accept drawdowns of up to 15% as part of the game."
  ],
  investment: [
    "I prioritize capital preservation over quick gains.",
    "I risk a maximum of 0.5% - 0.7% per trade.",
    "I wait for high-quality setups on higher timeframes (H4/Daily).",
    "I do not FOMO into moves; I wait for pullbacks.",
    "I stop trading if I reach a 5% drawdown in a month."
  ],
  flipper: [
    "I focus on flawless execution and low costs.",
    "I risk small (0.25% - 0.5%) to survive the frequency.",
    "I have a hard daily loss limit (e.g., -1.5%).",
    "I do not hold losing positions; I cut them instantly.",
    "I review every session to ensure I followed the process."
  ]
};

const PROFILE_LABELS = {
  risk_high: "Growth / High Risk (Aggressive)",
  investment: "Investment / Swing (Conservative)",
  flipper: "Flipper / Scalp (High Frequency)"
};

const TradingProfile = React.forwardRef(({ isEmbedded = false, isEditing = false, profileData = null, onSave = null }, ref) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Scroll State
  const contentRef = useRef(null);
  const scrollRailYRef = useRef(null);
  const [thumbHeight, setThumbHeight] = useState("38%");
  const [thumbTop, setThumbTop] = useState("0%");
  const [showScrollY, setShowScrollY] = useState(false);
  const isDraggingY = useRef(false);
  const startY = useRef(0);
  const startScrollTop = useRef(0);

  // State
  const [profileType, setProfileType] = useState('investment');
  const [riskPerTrade, setRiskPerTrade] = useState(0.5);
  const [annualObjective, setAnnualObjective] = useState(20);
  const [marginalizationLimit, setMarginalizationLimit] = useState(20);
  const [reviewLimit, setReviewLimit] = useState(5);
  const [selectedRules, setSelectedRules] = useState({});

  // Buffer state for cancel
  const [originalState, setOriginalState] = useState(null);

  // Load from props or localStorage on mount/update
  useEffect(() => {
    // If profileData is provided (from DB), use it
    if (profileData) {
      if (Array.isArray(profileData)) {
        // Parse list of rules from DB
        const rulesMap = {};
        const settings = {
          profileType: 'investment',
          riskPerTrade: 0.5,
          annualObjective: 20,
          marginalizationLimit: 20,
          reviewLimit: 5
        };

        profileData.forEach(r => {
          const name = r.rule_name;
          const val = r.rule_value;

          if (name === 'profileType') settings.profileType = val;
          else if (name === 'riskPerTrade') settings.riskPerTrade = parseFloat(val);
          else if (name === 'annualObjective') settings.annualObjective = parseFloat(val);
          else if (name === 'marginalizationLimit') settings.marginalizationLimit = parseFloat(val);
          else if (name === 'reviewLimit') settings.reviewLimit = parseFloat(val);
          else {
            // It's a rule checkbox
            rulesMap[name] = (val === 'true' || val === true);
          }
        });

        setProfileType(settings.profileType);
        setRiskPerTrade(settings.riskPerTrade);
        setAnnualObjective(settings.annualObjective);
        setMarginalizationLimit(settings.marginalizationLimit);
        setReviewLimit(settings.reviewLimit);
        setSelectedRules(rulesMap);
        return;
      } else if (typeof profileData === 'object' && Object.keys(profileData).length > 0) {
        // Handle draft account case (simple object)
        setProfileType(profileData.profileType || 'investment');
        setRiskPerTrade(profileData.riskPerTrade || 0.5);
        setAnnualObjective(profileData.annualObjective || 20);
        setMarginalizationLimit(profileData.marginalizationLimit || 20);
        setReviewLimit(profileData.reviewLimit || 5);
        setSelectedRules(profileData.selectedRules || {});
        return;
      }
    }

    // Fallback to localStorage if no prop data (legacy/standalone mode)
    const savedProfile = localStorage.getItem('tradingProfile');
    if (savedProfile && !profileData) {
      try {
        const parsed = JSON.parse(savedProfile);
        setProfileType(parsed.profileType || 'investment');
        setRiskPerTrade(parsed.riskPerTrade || 0.5);
        setAnnualObjective(parsed.annualObjective || 20);
        setMarginalizationLimit(parsed.marginalizationLimit || 20);
        setReviewLimit(parsed.reviewLimit || 5);
        setSelectedRules(parsed.selectedRules || {});
      } catch (e) {
        console.error("Failed to parse trading profile", e);
      }
    }
  }, [profileData]);

  // Sync isEditing prop
  useEffect(() => {
    if (isEditing) {
      setOriginalState({
        profileType,
        riskPerTrade,
        annualObjective,
        marginalizationLimit,
        reviewLimit,
        selectedRules: { ...selectedRules }
      });
    } else {
      setOriginalState(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  React.useImperativeHandle(ref, () => ({
    save: () => {
      // Flatten data for saving
      // We pass a dictionary which ProfileSettings will convert to list
      const data = {
        profileType,
        riskPerTrade,
        annualObjective,
        marginalizationLimit,
        reviewLimit,
        ...selectedRules // Spread rules mapping
      };

      if (onSave) {
        onSave(data);
      } else {
        // Legacy local storage save
        // But structure is different (flat vs nested). 
        // For legacy, let's keep nested structure if needed, but here we can just save flat?
        // The old code saved nested. Let's keep nested for localStorage to avoid breaking legacy?
        // Actually, we are moving away from localStorage.
        const legacyData = {
          profileType,
          riskPerTrade,
          annualObjective,
          marginalizationLimit,
          reviewLimit,
          selectedRules
        };
        localStorage.setItem('tradingProfile', JSON.stringify(legacyData));
      }
      setOriginalState(null);
    },
    cancel: () => {
      if (originalState) {
        setProfileType(originalState.profileType);
        setRiskPerTrade(originalState.riskPerTrade);
        setAnnualObjective(originalState.annualObjective);
        setMarginalizationLimit(originalState.marginalizationLimit);
        setReviewLimit(originalState.reviewLimit);
        setSelectedRules(originalState.selectedRules);
      }
      setOriginalState(null);
    }
  }));

  // Logic for Profile Change
  const handleProfileChange = (e) => {
    const newProfile = e.target.value;
    setProfileType(newProfile);
    setSelectedRules({}); // Reset rules

    // Set defaults based on profile
    if (newProfile === 'risk_high') {
      setRiskPerTrade(1.0);
      setAnnualObjective(50);
      setMarginalizationLimit(15);
      setReviewLimit(3);
    } else if (newProfile === 'investment') {
      setRiskPerTrade(0.5);
      setAnnualObjective(20);
      setMarginalizationLimit(20);
      setReviewLimit(5);
    } else if (newProfile === 'flipper') {
      setRiskPerTrade(0.25);
      setAnnualObjective(100);
      setMarginalizationLimit(30);
      setReviewLimit(5);
    }
  };

  const toggleRule = (rule) => {
    if (!isEditing) return;
    setSelectedRules(prev => ({
      ...prev,
      [rule]: !prev[rule]
    }));
  };

  // Scroll Logic
  const handleScroll = () => {
    if (contentRef.current) {
      if (isDraggingY.current) return;
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const maxScrollTop = scrollHeight - clientHeight;
      if (maxScrollTop > 0) {
        const scrollPercentageY = (scrollTop / maxScrollTop) * 100;
        const maxTop = 100 - parseFloat(thumbHeight);
        const top = (scrollPercentageY / 100) * maxTop;
        setThumbTop(`${top}%`);
      }
    }
  };

  const checkScroll = () => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      const hasScrollY = scrollHeight > clientHeight;
      setShowScrollY(hasScrollY);
      if (hasScrollY) {
        const heightPerc = (clientHeight / scrollHeight) * 100;
        const finalHeight = Math.max(heightPerc, 10);
        setThumbHeight(`${finalHeight}%`);
      }
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [profileType, selectedRules, isEditing]); // Re-check on state changes that affect height

  const handleMouseDownY = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingY.current = true;
    startY.current = e.clientY;
    startScrollTop.current = contentRef.current.scrollTop;
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (isDraggingY.current && contentRef.current && scrollRailYRef.current) {
      e.preventDefault();
      const { scrollHeight, clientHeight } = contentRef.current;
      const railHeight = scrollRailYRef.current.clientHeight;
      const thumbHeightPx = (parseFloat(thumbHeight) / 100) * railHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      const availableRail = railHeight - thumbHeightPx;
      const deltaY = e.clientY - startY.current;
      const scrollRatio = maxScrollTop / availableRail;
      contentRef.current.scrollTop = startScrollTop.current + (deltaY * scrollRatio);

      // Update local thumb position immediately for smoothness
      const scrollPercentageY = (contentRef.current.scrollTop / maxScrollTop) * 100;
      const maxTop = 100 - parseFloat(thumbHeight);
      const top = (scrollPercentageY / 100) * maxTop;
      setThumbTop(`${top}%`);
    }
  };

  const handleMouseUp = () => {
    isDraggingY.current = false;
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Calculations
  const currentRules = RULES_BY_PROFILE[profileType] || [];
  const progress = currentRules.length > 0
    ? (currentRules.filter(r => selectedRules[r]).length / currentRules.length) * 100
    : 0;

  const marginalizationDrawdown = (riskPerTrade * marginalizationLimit).toFixed(1);
  const reviewDrawdown = (riskPerTrade * reviewLimit).toFixed(1);
  const impliedTradesToObjective = riskPerTrade > 0 ? (annualObjective / (riskPerTrade * 2)) : 0;

  const content = (
    <CenteredLayout>
      <ScrollWrapper ref={contentRef} onScroll={handleScroll}>
        <Container>
          <CalculatorGrid>
            {/* Left Column: Settings */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <SectionBlock>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px' }}>
                  <SectionTitle style={{ border: 'none', padding: 0, margin: 0 }}>Profile Settings</SectionTitle>
                </div>

                <InputGroup>
                  <Label>Trading Style (Profile)</Label>
                  {isEditing ? (
                    <StyledSelect
                      value={profileType}
                      onChange={handleProfileChange}
                      theme={theme}
                    >
                      <option value="risk_high">Growth / High Risk (Aggressive)</option>
                      <option value="investment">Investment / Swing (Conservative)</option>
                      <option value="flipper">Flipper / Scalp (High Frequency)</option>
                    </StyledSelect>
                  ) : (
                    <Value>{PROFILE_LABELS[profileType]}</Value>
                  )}
                </InputGroup>

                <ResponsiveGrid2>
                  <InputGroup>
                    <Label>Annual Objective (%)</Label>
                    {isEditing ? (
                      <StyledInput
                        type="number"
                        value={annualObjective}
                        onChange={(e) => setAnnualObjective(parseFloat(e.target.value) || 0)}
                        theme={theme}
                      />
                    ) : (
                      <Value>{annualObjective}%</Value>
                    )}
                  </InputGroup>
                  <InputGroup>
                    <Label>Risk Per Trade (%)</Label>
                    {isEditing ? (
                      <StyledInput
                        type="number"
                        step="0.1"
                        value={riskPerTrade}
                        onChange={(e) => setRiskPerTrade(parseFloat(e.target.value) || 0)}
                        theme={theme}
                      />
                    ) : (
                      <Value>{riskPerTrade}%</Value>
                    )}
                  </InputGroup>
                </ResponsiveGrid2>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>Psychology & Risk Limits</SectionTitle>
                <ResponsiveGrid2>
                  <InputGroup>
                    <Label>Marginalization Limit</Label>
                    {isEditing ? (
                      <StyledInput
                        type="number"
                        value={marginalizationLimit}
                        onChange={(e) => setMarginalizationLimit(parseFloat(e.target.value) || 0)}
                        theme={theme}
                      />
                    ) : (
                      <Value>{marginalizationLimit} trades</Value>
                    )}
                    <div style={{ fontSize: '10px', color: colors.grey[400], marginTop: '2px' }}>
                      Trades to ruin/marginalize account
                    </div>
                  </InputGroup>
                  <InputGroup>
                    <Label>Strategy Review Limit</Label>
                    {isEditing ? (
                      <StyledInput
                        type="number"
                        value={reviewLimit}
                        onChange={(e) => setReviewLimit(parseFloat(e.target.value) || 0)}
                        theme={theme}
                      />
                    ) : (
                      <Value>{reviewLimit} trades</Value>
                    )}
                    <div style={{ fontSize: '10px', color: colors.grey[400], marginTop: '2px' }}>
                      Consecutive losses to stop & review
                    </div>
                  </InputGroup>
                </ResponsiveGrid2>
              </SectionBlock>

              <SectionBlock>
                <SectionTitle>Risk Analysis</SectionTitle>
                <StatGrid>
                  <StatBox theme={theme}>
                    <StatValue theme={theme} color={colors.redAccent[400]}>{marginalizationDrawdown}%</StatValue>
                    <StatLabel theme={theme}>Marginalization DD</StatLabel>
                  </StatBox>
                  <StatBox theme={theme}>
                    <StatValue theme={theme} color={colors.blueAccent[400]}>{reviewDrawdown}%</StatValue>
                    <StatLabel theme={theme}>Review Trigger DD</StatLabel>
                  </StatBox>
                  <StatBox theme={theme}>
                    <StatValue theme={theme} color={colors.greenAccent[400]}>~{Math.round(impliedTradesToObjective)}</StatValue>
                    <StatLabel theme={theme}>Wins Needed (1:2)</StatLabel>
                  </StatBox>
                </StatGrid>
                <div style={{ fontSize: '11px', color: colors.grey[300], marginTop: '8px', lineHeight: '1.4' }}>
                  <strong>Psychology Check:</strong>
                  <ul style={{ paddingLeft: '16px', margin: '4px 0' }}>
                    <li>If you lose <strong>{reviewLimit}</strong> trades in a row (-{reviewDrawdown}%), you MUST stop and review.</li>
                    <li>Your account is considered marginalized after <strong>{marginalizationLimit}</strong> consecutive losses (-{marginalizationDrawdown}%).</li>
                  </ul>
                </div>
              </SectionBlock>
            </div>

            {/* Right Column: Discipline Rules */}
            <SectionBlock style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <SectionTitle>Discipline Rules</SectionTitle>
                <div style={{ color: progress === 100 ? colors.greenAccent[500] : colors.grey[400], fontSize: '12px', fontWeight: 'bold' }}>
                  {Math.round(progress)}% Committed
                </div>
              </div>

              <div style={{ width: '100%', height: '4px', background: colors.grey[700], borderRadius: '2px', marginBottom: '12px' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: colors.greenAccent[500], borderRadius: '2px', transition: 'width 0.3s' }} />
              </div>

              <RulesList>
                {currentRules.map((rule, index) => (
                  <RuleItem
                    key={index}
                    checked={selectedRules[rule]}
                    onClick={() => toggleRule(rule)}
                    disabled={!isEditing}
                    theme={theme}
                  >
                    <Checkbox checked={selectedRules[rule]} disabled={!isEditing} theme={theme} />
                    <RuleText theme={theme}>{rule}</RuleText>
                  </RuleItem>
                ))}
              </RulesList>
            </SectionBlock>
          </CalculatorGrid>
        </Container>
      </ScrollWrapper>

      {showScrollY && (
        <>
          <ScrollRailVertical ref={scrollRailYRef} onMouseDown={handleMouseDownY} style={{ top: 0, bottom: 0, right: '4px' }} />
          <ScrollThumbVertical
            height={thumbHeight}
            top={thumbTop}
            onMouseDown={handleMouseDownY}
            style={{ left: 'auto', right: '5px', transform: 'none' }}
          />
        </>
      )}
    </CenteredLayout>
  );

  if (isEmbedded) {
    return content;
  }

  // Standalone mode not usually used anymore but kept for safety
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Trading Profile & Discipline</PanelTitle>
      </PanelHeader>
      {content}
    </Panel>
  );
});

export default TradingProfile;
